/**
 * lib/theme.ts
 * ------------------------------------------------------------------
 * Dark-mode support. Three modes:
 *   - 'light'   force light
 *   - 'dark'    force dark
 *   - 'system'  follow OS preference (prefers-color-scheme)
 *
 * Persistence: localStorage under 'acepsle.theme'. Default is 'system'.
 *
 * The `dark` class on <html> is what Tailwind's `dark:` variants key
 * off (darkMode: 'class'). This module is the single source of truth
 * for adding/removing that class.
 *
 * A pre-paint script in index.html applies the same logic before React
 * mounts, to avoid a flash of light theme for a dark-mode user. Keep
 * this module's resolution logic and that script in sync.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'acepsle.theme';

export function getStoredTheme(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* localStorage blocked — fall through to default */
  }
  return 'light';
}

export function setTheme(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* localStorage blocked — still update the DOM in-memory */
  }
  applyTheme(mode);
}

export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return mode;
}

export function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const effective = resolveTheme(mode);
  if (effective === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

/**
 * React hook for theme state. Returns [mode, setMode, effective].
 * When mode is 'system', subscribes to prefers-color-scheme changes.
 */
export function useTheme(): {
  mode: ThemeMode;
  setMode: (next: ThemeMode) => void;
  effective: 'light' | 'dark';
} {
  const [mode, setModeState] = useState<ThemeMode>(() => getStoredTheme());
  const [effective, setEffective] = useState<'light' | 'dark'>(() =>
    resolveTheme(getStoredTheme()),
  );

  useEffect(() => {
    if (mode !== 'system' || typeof window === 'undefined') {
      setEffective(resolveTheme(mode));
      return;
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      applyTheme('system');
      setEffective(mql.matches ? 'dark' : 'light');
    };
    setEffective(mql.matches ? 'dark' : 'light');
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [mode]);

  function setMode(next: ThemeMode) {
    setTheme(next);
    setModeState(next);
    setEffective(resolveTheme(next));
  }

  return { mode, setMode, effective };
}
